import { useEffect, useState, useCallback } from 'react';
import { get } from '../../lib/api-client';

interface UserEntry {
  id: string;
  email: string;
  tier: string;
  teamId: string | null;
  analysisCount: number;
  createdAt: string;
}

interface UsersResponse {
  data: UserEntry[];
  pagination: { page: number; pageSize: number; total: number };
}

export function Users() {
  const [users, setUsers] = useState<UserEntry[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const pageSize = 20;

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(pageSize) });
      if (search) params.set('search', search);
      if (tierFilter) params.set('tier', tierFilter);
      const data = await get<UsersResponse>(`/v1/admin/users?${params.toString()}`);
      setUsers(data.data);
      setTotal(data.pagination.total);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [page, search, tierFilter]);

  useEffect(() => {
    void fetchUsers();
  }, [fetchUsers]);

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-ll-text">Users</h2>

      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="text"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          placeholder="Search by email..."
          className="flex-1 bg-ll-bg border border-ll-border rounded-lg px-3 py-2 text-sm text-ll-text placeholder-ll-muted/50 focus:outline-none focus:ring-2 focus:ring-ll-cyan"
        />
        <select
          value={tierFilter}
          onChange={(e) => {
            setTierFilter(e.target.value);
            setPage(1);
          }}
          className="bg-ll-bg border border-ll-border rounded-lg px-3 py-2 text-sm text-ll-text focus:outline-none focus:ring-2 focus:ring-ll-cyan"
        >
          <option value="">All tiers</option>
          <option value="free">Free</option>
          <option value="pro">Pro</option>
          <option value="team">Team</option>
          <option value="enterprise">Enterprise</option>
        </select>
      </div>

      {error && (
        <div className="bg-ll-error/10 border border-ll-error/30 rounded-lg p-4 text-sm text-ll-error">
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-ll-muted">Loading...</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ll-border text-left">
                  <th className="py-2 px-3 text-xs uppercase font-mono text-ll-muted">Email</th>
                  <th className="py-2 px-3 text-xs uppercase font-mono text-ll-muted">Tier</th>
                  <th className="py-2 px-3 text-xs uppercase font-mono text-ll-muted">Analyses</th>
                  <th className="py-2 px-3 text-xs uppercase font-mono text-ll-muted">Team</th>
                  <th className="py-2 px-3 text-xs uppercase font-mono text-ll-muted">Created</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-b border-ll-border/50 hover:bg-white/[0.02]">
                    <td className="py-2.5 px-3 text-ll-text font-mono text-xs">{user.email}</td>
                    <td className="py-2.5 px-3">
                      <span className="text-xs font-bold uppercase px-1.5 py-0.5 rounded bg-ll-muted/20 text-ll-muted">
                        {user.tier}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 text-ll-text">{user.analysisCount}</td>
                    <td className="py-2.5 px-3 text-ll-muted text-xs font-mono">
                      {user.teamId ?? '--'}
                    </td>
                    <td className="py-2.5 px-3 text-ll-muted">
                      {new Date(user.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-ll-muted">
                      No users found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-xs text-ll-muted">{total} users total</p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="px-3 py-1.5 rounded border border-ll-border text-xs text-ll-muted hover:text-ll-text disabled:opacity-30 transition-colors"
                >
                  Previous
                </button>
                <span className="text-xs text-ll-muted py-1.5">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="px-3 py-1.5 rounded border border-ll-border text-xs text-ll-muted hover:text-ll-text disabled:opacity-30 transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
